import React, { useState, useCallback } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  FunnelChart,
  Funnel,
  Area,
  AreaChart,
} from "recharts";

// KPI Card Component
const KPICard = ({ title, value, trend, change, color, onClick }) => (
  <div
    onClick={onClick}
    className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm transition-all duration-200 hover:shadow-lg dark:shadow-none cursor-pointer"
  >
    <div className="flex justify-between items-start">
      <h3 className="text-gray-500 dark:text-gray-500 text-sm font-medium">{title}</h3>
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          trend === "up"
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
        }`}
      >
        {change}
      </span>
    </div>
    <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
  </div>
);

const FirmAnalytics = () => {
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Firm Analytics", link: "/administrative/firm-analytics" },
  ];

  // Mock data for KPIs
  const kpiData = [
    {
      title: "Total Sales",
      value: "$2.5M",
      trend: "up",
      change: "+15%",
      color: "green",
    },
    {
      title: "New Clients",
      value: "127",
      trend: "up",
      change: "+23%",
      color: "green",
    },
    {
      title: "Active Campaigns",
      value: "8",
      trend: "down",
      change: "-2%",
      color: "red",
    },
    {
      title: "Client Retention",
      value: "94%",
      trend: "up",
      change: "+3%",
      color: "green",
    },
    {
      title: "Employee Productivity",
      value: "87%",
      trend: "up",
      change: "+5%",
      color: "green",
    },
  ];

  // Mock data for revenue trend
  const revenueData = [
    { month: "Jan", revenue: 150000 },
    { month: "Feb", revenue: 180000 },
    { month: "Mar", revenue: 210000 },
    { month: "Apr", revenue: 240000 },
    { month: "May", revenue: 270000 },
    { month: "Jun", revenue: 320000 },
  ];

  // Mock data for sales breakdown
  const salesData = [
    { name: "Investment Advisory", value: 45 },
    { name: "Financial Planning", value: 30 },
    { name: "Insurance", value: 15 },
    { name: "Tax Services", value: 10 },
  ];

  // Mock data for campaign performance
  const campaignData = [
    { name: "Email Campaign", spend: 5000, roi: 280 },
    { name: "Social Media", spend: 3000, roi: 320 },
    { name: "Webinar Series", spend: 8000, roi: 420 },
    { name: "Client Referral", spend: 2000, roi: 580 },
  ];

  // Mock data for employee performance
  const employeeData = [
    { name: "John Doe", sales: 450000, clients: 45, rating: 4.8 },
    { name: "Jane Smith", sales: 380000, clients: 38, rating: 4.6 },
    { name: "Mike Johnson", sales: 520000, clients: 52, rating: 4.9 },
    { name: "Sarah Wilson", sales: 410000, clients: 41, rating: 4.7 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  // New state for filters and interactivity
  const [timeRange, setTimeRange] = useState("last30Days");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Client acquisition data
  const clientAcquisitionData = [
    { month: "Jan", newClients: 12, churnedClients: 2, retentionRate: 95 },
    { month: "Feb", newClients: 15, churnedClients: 1, retentionRate: 97 },
    { month: "Mar", newClients: 18, churnedClients: 3, retentionRate: 94 },
    { month: "Apr", newClients: 22, churnedClients: 2, retentionRate: 96 },
    { month: "May", newClients: 20, churnedClients: 4, retentionRate: 93 },
    { month: "Jun", newClients: 25, churnedClients: 3, retentionRate: 95 },
  ];

  // Acquisition channel data
  const acquisitionChannelData = [
    { value: 35, name: "Referrals", fill: "#8884d8" },
    { value: 25, name: "Digital Marketing", fill: "#83a6ed" },
    { value: 20, name: "Direct Outreach", fill: "#8dd1e1" },
    { value: 15, name: "Events", fill: "#82ca9d" },
    { value: 5, name: "Other", fill: "#a4de6c" },
  ];

  // Handler for KPI card clicks
  const handleKPIClick = useCallback((metric) => {
    setActiveTab(metric.toLowerCase());
  }, []);

  return (
    <SubPageLayout
      title="Firm Analytics"
      subtitle="Track and analyze your firm's performance metrics"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-8 p-6">
        {/* Filter Controls */}
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-sm mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <select
                className="w-full border-gray-300 rounded-lg text-gray-900 dark:text-gray-100"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option className="text-gray-900 dark:text-gray-100">Last 7 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Last 30 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Last 90 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Custom Range</option>
              </select>
            </div>
            <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
              Refresh Data
            </button>
          </div>
        </div>

        {/* Top Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {kpiData.map((kpi, index) => (
            <KPICard
              key={index}
              {...kpi}
              onClick={() => handleKPIClick(kpi.title)}
            />
          ))}
        </div>

        {/* Sales and Revenue Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Revenue Trend
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#202EC6"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Sales Breakdown
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={salesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {salesData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Client Acquisition and Retention */}
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Client Acquisition & Retention
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={clientAcquisitionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="newClients"
                    stackId="1"
                    stroke="#8884d8"
                    fill="#8884d8"
                  />
                  <Area
                    type="monotone"
                    dataKey="churnedClients"
                    stackId="1"
                    stroke="#82ca9d"
                    fill="#82ca9d"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <FunnelChart>
                  <Tooltip />
                  <Funnel
                    dataKey="value"
                    data={acquisitionChannelData}
                    isAnimationActive
                  >
                    {acquisitionChannelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Campaign Performance */}
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Campaign Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Spend
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    ROI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
                {campaignData.map((campaign, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {campaign.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                      ${campaign.spend}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                      {campaign.roi}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Employee Performance */}
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Employee Performance
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={employeeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sales" fill="#202EC6" />
                    <Bar dataKey="clients" fill="#029831" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
                Top Performers
              </h4>
              {employeeData
                .sort((a, b) => b.sales - a.sales)
                .slice(0, 3)
                .map((employee, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {employee.name}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">
                        ${(employee.sales / 1000).toFixed(0)}k
                      </span>
                    </div>
                    <div className="mt-1 relative pt-1">
                      <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                        <div
                          style={{
                            width: `${(employee.sales / 520000) * 100}%`,
                          }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#202EC6]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Custom Reporting */}
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Custom Reports
            </h3>
            <div className="flex space-x-2">
              <button className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
                Save Template
              </button>
              <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
                Generate Report
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Date Range
              </label>
              <select className="w-full border-gray-300 rounded-lg shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] text-gray-900 dark:text-gray-100">
                <option className="text-gray-900 dark:text-gray-100">Last 7 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Last 30 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Last 90 Days</option>
                <option className="text-gray-900 dark:text-gray-100">Custom Range</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Report Type
              </label>
              <select className="w-full border-gray-300 rounded-lg shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] text-gray-900 dark:text-gray-100">
                <option className="text-gray-900 dark:text-gray-100">Sales Analysis</option>
                <option className="text-gray-900 dark:text-gray-100">Client Metrics</option>
                <option className="text-gray-900 dark:text-gray-100">Campaign Performance</option>
                <option className="text-gray-900 dark:text-gray-100">Employee Performance</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Export Format
              </label>
              <select className="w-full border-gray-300 rounded-lg shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] text-gray-900 dark:text-gray-100">
                <option className="text-gray-900 dark:text-gray-100">PDF</option>
                <option className="text-gray-900 dark:text-gray-100">Excel</option>
                <option className="text-gray-900 dark:text-gray-100">CSV</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Report Elements
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                "Revenue Trends",
                "Client Metrics",
                "Campaign Stats",
                "Employee Data",
              ].map((element, index) => (
                <div key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-[#202EC6] focus:ring-[#202EC6] border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {element}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default FirmAnalytics;
